import React, { useState, useContext, useMemo } from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { gql, useQuery, useMutation } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import "../../starek.css";
import DataTable from "react-data-table-component";

const Settings = () => {
    const starekContext = useContext(StarekContext);

    const GET_SETTINGS_FOR_USER = gql`
        query GetSettings($userId: Int!) {
            settings {
                id
                code
                name
                meta
                user_preferences(where: { owned_by_id: { equals: $userId } }) {
                    value
                    owned_by {
                        name
                    }
                }
            }
        }
    `;

    const UPSERT_USER_PREFERENCE = gql`
        mutation UpdateManyPreference(
            $data: PreferenceUpdateManyMutationInput!
            $where: PreferenceWhereInput!
        ) {
            updateManyPreference(data: $data, where: $where) {
                count
            }
        }
    `;

    const CREATE_USER_PREFERENCE = gql`
        mutation CreateOnePreference($data: PreferenceCreateInput!) {
            createOnePreference(data: $data) {
                value
                setting {
                    name
                }
                owned_by {
                    id
                    name
                }
            }
        }
    `;

    const DELETE_USER_PREFERENCE = gql`
        mutation DeleteManyPreference($where: PreferenceWhereInput!) {
            deleteManyPreference(where: $where) {
                count
            }
        }
    `;

    // State to track loading states for individual settings
    const [loadingSettings, setLoadingSettings] = useState({});

    const { data, loading, refetch } = useQuery(GET_SETTINGS_FOR_USER, {
        client: starekContext.engineClient,
        variables: {
            userId: parseInt(starekContext.user.id),
        },
    });

    const [updateManyPreference] = useMutation(UPSERT_USER_PREFERENCE, {
        client: starekContext.engineClient,
    });

    const [createOnePreference] = useMutation(CREATE_USER_PREFERENCE, {
        client: starekContext.engineClient,
    });

    const [deleteManyPreference] = useMutation(DELETE_USER_PREFERENCE, {
        client: starekContext.engineClient,
    });

    const [filterText, setFilterText] = useState("");
    const [showColumns, setShowColumns] = useState([true, true, false]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("all");

    // State to track validation errors for each setting
    const [validationErrors, setValidationErrors] = useState({});

    const settings = data?.settings ?? [];

    // Get unique categories from settings
    const categories = useMemo(() => {
        const cats = [
            ...new Set(settings.map((s) => s.meta?.category).filter(Boolean)),
        ];
        return ["all", ...cats.sort()];
    }, [settings]);

    // Validation function
    const validateSetting = (value, setting) => {
        const validation = setting.meta?.validation;
        const settingType = setting.meta?.type;

        if (!validation) return null; // No validation rules defined

        switch (settingType) {
            case "string":
                if (validation.required && (!value || value.trim() === "")) {
                    return "This field is required";
                }
                if (
                    validation.minLength &&
                    value.length < validation.minLength
                ) {
                    return `Minimum length is ${validation.minLength} characters`;
                }
                if (
                    validation.maxLength &&
                    value.length > validation.maxLength
                ) {
                    return `Maximum length is ${validation.maxLength} characters`;
                }
                if (
                    validation.pattern &&
                    !new RegExp(validation.pattern).test(value)
                ) {
                    return validation.patternMessage || "Invalid format";
                }
                break;

            case "integer":
                const numValue = parseInt(value);
                if (isNaN(numValue)) {
                    return "Must be a valid number";
                }
                if (validation.min !== undefined && numValue < validation.min) {
                    return `Minimum value is ${validation.min}`;
                }
                if (validation.max !== undefined && numValue > validation.max) {
                    return `Maximum value is ${validation.max}`;
                }
                break;

            case "single_select":
                if (validation.required && (!value || value === "")) {
                    return "Please select a value";
                }
                if (validation.options && !validation.options.includes(value)) {
                    return "Invalid selection";
                }
                break;
        }

        return null; // No validation errors
    };

    const handleSaveSetting = async (settingName, value) => {
        try {
            // Find the setting to get its id
            const setting = settings.find((s) => s.name === settingName);
            if (!setting) return;

            // Validate the value
            const validationError = validateSetting(value, setting);
            if (validationError) {
                setValidationErrors((prev) => ({
                    ...prev,
                    [settingName]: validationError,
                }));
                return; // Don't save if validation fails
            } else {
                // Clear validation error if validation passes
                setValidationErrors((prev) => {
                    const next = { ...prev };
                    delete next[settingName];
                    return next;
                });
            }

            // Set loading state for this setting
            setLoadingSettings((prev) => ({ ...prev, [settingName]: true }));

            // Check if user preference already exists
            const existingPreference = setting.user_preferences?.[0];

            if (existingPreference) {
                // Update existing preference using updateManyPreference
                await updateManyPreference({
                    variables: {
                        where: {
                            AND: [
                                {
                                    setting_id: {
                                        equals: setting.id,
                                    },
                                },
                                {
                                    owned_by_id: {
                                        equals: parseInt(starekContext.user.id),
                                    },
                                },
                            ],
                        },
                        data: {
                            value: {
                                set: String(value),
                            },
                        },
                    },
                });
            } else {
                // Create new preference
                await createOnePreference({
                    variables: {
                        data: {
                            value: String(value),
                            setting: {
                                connect: {
                                    id: setting.id,
                                },
                            },
                            owned_by: {
                                connect: {
                                    id: parseInt(starekContext.user.id),
                                },
                            },
                            created_by: {
                                connect: {
                                    id: parseInt(starekContext.user.id),
                                },
                            },
                            updated_by: {
                                connect: {
                                    id: parseInt(starekContext.user.id),
                                },
                            },
                        },
                    },
                });
            }

            // Refetch to get updated data
            await refetch();
        } catch (error) {
            console.error("Error saving setting:", error);
            starekContext.showError("Failed to save setting: " + error.message);
        } finally {
            // Clear loading state for this setting
            setLoadingSettings((prev) => {
                const next = { ...prev };
                delete next[settingName];
                return next;
            });
        }
    };

    const filteredItems = useMemo(() => {
        let filtered = settings;

        // Filter by category
        if (selectedCategory !== "all") {
            filtered = filtered.filter(
                (item) => item.meta?.category === selectedCategory
            );
        }

        // Filter by search text
        const q = filterText.trim().toLowerCase();
        if (q) {
            filtered = filtered.filter(
                (item) =>
                    item.name?.toLowerCase().includes(q) ||
                    item.description?.toLowerCase().includes(q) ||
                    item.code?.toLowerCase().includes(q)
            );
        }

        return filtered;
    }, [settings, filterText, selectedCategory]);

    const toggleColumnVisibility = (index) => {
        setShowColumns((prev) => {
            const next = [...prev];
            next[index] = !next[index];
            return next;
        });
    };

    const columns = [
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            width: "60%",
            cell: (row) => (
                <div className="d-flex align-items-center">
                    <span className="w-30px h-30px me-2 d-flex align-items-center justify-content-center rounded-circle border bg-color-f2f2f2">
                        <i className="fa fa-cog"></i>
                    </span>
                    <div className="d-flex flex-column">
                        <span>{row.name}</span>
                        <small className="text-muted">({row.code})</small>
                    </div>
                </div>
            ),
        },
        {
            name: "Value",
            width: "30%",
            right: true,
            cell: (row) => {
                const currentValue = row.user_preferences?.[0]?.value;
                const defaultValue = row.meta?.default;
                const settingType = row.meta?.type;
                const isLoading = loadingSettings[row.name];

                switch (settingType) {
                    case "string":
                        const hasValidationError = validationErrors[row.name];
                        return (
                            <div
                                style={{
                                    width: "100%",
                                    display: "block",
                                    position: "relative",
                                }}
                            >
                                <input
                                    key={`${row.id}-${
                                        currentValue || "default"
                                    }`}
                                    type="text"
                                    className={`form-control form-control-sm ${
                                        hasValidationError ? "is-invalid" : ""
                                    }`}
                                    style={{
                                        width: "100%",
                                        textAlign: "right",
                                        boxSizing: "border-box",
                                        opacity: isLoading ? 0.6 : 1,
                                    }}
                                    defaultValue={
                                        currentValue || defaultValue || ""
                                    }
                                    onBlur={(e) => {
                                        const newValue = e.target.value;
                                        const compareValue =
                                            currentValue || defaultValue || "";
                                        if (newValue !== compareValue) {
                                            handleSaveSetting(
                                                row.name,
                                                newValue
                                            );
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            const newValue = e.target.value;
                                            const compareValue =
                                                currentValue ||
                                                defaultValue ||
                                                "";
                                            if (newValue !== compareValue) {
                                                handleSaveSetting(
                                                    row.name,
                                                    newValue
                                                );
                                            }
                                            e.target.blur(); // Remove focus to prevent double save
                                        }
                                    }}
                                    placeholder=""
                                    pattern={row.meta?.validation}
                                    disabled={isLoading}
                                />
                                {hasValidationError && (
                                    <div
                                        className="invalid-feedback"
                                        style={{
                                            display: "block",
                                            fontSize: "11px",
                                            textAlign: "right",
                                            marginTop: "2px",
                                        }}
                                    >
                                        {hasValidationError}
                                    </div>
                                )}
                                <style>{`
                                    input::placeholder {
                                        font-style: italic;
                                    }
                                `}</style>
                            </div>
                        );

                    case "integer":
                        const hasIntValidationError =
                            validationErrors[row.name];
                        return (
                            <div
                                style={{
                                    width: "100%",
                                    display: "block",
                                    position: "relative",
                                }}
                            >
                                <input
                                    key={`${row.id}-${
                                        currentValue || "default"
                                    }`}
                                    type="number"
                                    className={`form-control form-control-sm ${
                                        hasIntValidationError
                                            ? "is-invalid"
                                            : ""
                                    }`}
                                    style={{
                                        width: "100%",
                                        textAlign: "right",
                                        boxSizing: "border-box",
                                        opacity: isLoading ? 0.6 : 1,
                                    }}
                                    defaultValue={
                                        currentValue || defaultValue || ""
                                    }
                                    onBlur={(e) => {
                                        const newValue = parseInt(
                                            e.target.value
                                        );
                                        const currentNumValue = currentValue
                                            ? parseInt(currentValue)
                                            : defaultValue
                                            ? parseInt(defaultValue)
                                            : null;
                                        if (
                                            newValue !== currentNumValue &&
                                            !isNaN(newValue)
                                        ) {
                                            handleSaveSetting(
                                                row.name,
                                                newValue
                                            );
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            const newValue = parseInt(
                                                e.target.value
                                            );
                                            const currentNumValue = currentValue
                                                ? parseInt(currentValue)
                                                : defaultValue
                                                ? parseInt(defaultValue)
                                                : null;
                                            if (
                                                newValue !== currentNumValue &&
                                                !isNaN(newValue)
                                            ) {
                                                handleSaveSetting(
                                                    row.name,
                                                    newValue
                                                );
                                            }
                                            e.target.blur(); // Remove focus to prevent double save
                                        }
                                    }}
                                    placeholder=""
                                    min={row.meta?.validation?.min}
                                    max={row.meta?.validation?.max}
                                    disabled={isLoading}
                                />
                                {hasIntValidationError && (
                                    <div
                                        className="invalid-feedback"
                                        style={{
                                            display: "block",
                                            fontSize: "11px",
                                            textAlign: "right",
                                            marginTop: "2px",
                                        }}
                                    >
                                        {hasIntValidationError}
                                    </div>
                                )}
                                <style>{`
                                    input::placeholder {
                                        font-style: italic;
                                    }
                                `}</style>
                            </div>
                        );

                    case "boolean":
                        const isChecked =
                            currentValue !== undefined
                                ? currentValue === true ||
                                  currentValue === "true"
                                : defaultValue === true ||
                                  defaultValue === "true";

                        return (
                            <div
                                className="d-flex align-items-center justify-content-end"
                                style={{ marginRight: "20px" }}
                            >
                                <div
                                    className="form-check form-switch"
                                    style={{ position: "relative" }}
                                >
                                    <input
                                        key={`${row.id}-${
                                            currentValue || "default"
                                        }`}
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={isChecked}
                                        onChange={(e) => {
                                            handleSaveSetting(
                                                row.name,
                                                e.target.checked
                                            );
                                        }}
                                        style={{
                                            backgroundColor: isChecked
                                                ? "#0d6efd"
                                                : "#6c757d",
                                            borderColor: isChecked
                                                ? "#0d6efd"
                                                : "#6c757d",
                                            width: "3rem",
                                            height: "1.5rem",
                                            fontSize: "1rem",
                                            borderRadius: "0.75rem",
                                            opacity: isLoading ? 0.6 : 1,
                                        }}
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        );

                    case "single_select":
                    case "select":
                        const hasSelectValidationError =
                            validationErrors[row.name];
                        const selectOptions =
                            row.meta?.choices || row.meta?.options || [];
                        return (
                            <div
                                style={{
                                    width: "100%",
                                    display: "block",
                                    position: "relative",
                                }}
                            >
                                <select
                                    key={`${row.id}-${
                                        currentValue || "default"
                                    }`}
                                    className={`form-select form-select-sm ${
                                        hasSelectValidationError
                                            ? "is-invalid"
                                            : ""
                                    }`}
                                    style={{
                                        width: "100%",
                                        boxSizing: "border-box",
                                        opacity: isLoading ? 0.6 : 1,
                                    }}
                                    value={currentValue || defaultValue || ""}
                                    onChange={(e) => {
                                        const compareValue =
                                            currentValue || defaultValue || "";
                                        if (e.target.value !== compareValue) {
                                            handleSaveSetting(
                                                row.name,
                                                e.target.value
                                            );
                                        }
                                    }}
                                    disabled={isLoading}
                                >
                                    <option value="">Select...</option>
                                    {selectOptions.map((option) => {
                                        // Handle both { label, value } format and simple string format
                                        const optionValue =
                                            typeof option === "object"
                                                ? option.value
                                                : option;
                                        const optionLabel =
                                            typeof option === "object"
                                                ? option.label
                                                : option;
                                        return (
                                            <option
                                                key={optionValue}
                                                value={optionValue}
                                            >
                                                {optionLabel}
                                            </option>
                                        );
                                    })}
                                </select>
                                {hasSelectValidationError && (
                                    <div
                                        className="invalid-feedback"
                                        style={{
                                            display: "block",
                                            fontSize: "11px",
                                            textAlign: "right",
                                            marginTop: "2px",
                                        }}
                                    >
                                        {hasSelectValidationError}
                                    </div>
                                )}
                            </div>
                        );

                    default:
                        return (
                            <span className="text-muted fst-italic">
                                Unsupported type: {settingType}
                            </span>
                        );
                }
            },
        },
        {
            name: "Revert",
            width: "10%",
            center: true,
            cell: (row) => {
                const defaultValue = row.meta?.default;
                const currentValue = row.user_preferences?.[0]?.value;
                const isLoading = loadingSettings[row.name];

                // Check if current effective value equals default value
                const effectiveValue =
                    currentValue !== undefined ? currentValue : defaultValue;
                const isAtDefault =
                    !row.user_preferences?.[0] ||
                    effectiveValue === defaultValue;

                const handleRevertToDefault = async () => {
                    try {
                        // Set loading state for this setting
                        setLoadingSettings((prev) => ({
                            ...prev,
                            [row.name]: true,
                        }));

                        // Check if user preference exists
                        const existingPreference = row.user_preferences?.[0];

                        if (existingPreference) {
                            // Delete the user preference to revert to default
                            await deleteManyPreference({
                                variables: {
                                    where: {
                                        AND: [
                                            {
                                                setting_id: {
                                                    equals: row.id,
                                                },
                                            },
                                            {
                                                owned_by_id: {
                                                    equals: parseInt(
                                                        starekContext.user.id
                                                    ),
                                                },
                                            },
                                        ],
                                    },
                                },
                            });

                            // Refetch to update the UI
                            await refetch();

                            starekContext.showSuccess(
                                `Setting "${row.name}" reverted to default`
                            );
                        }
                    } catch (error) {
                        console.error("Error reverting setting:", error);
                        starekContext.showError(
                            "Failed to revert setting: " + error.message
                        );
                    } finally {
                        // Clear loading state for this setting
                        setLoadingSettings((prev) => {
                            const next = { ...prev };
                            delete next[row.name];
                            return next;
                        });
                    }
                };

                return (
                    <button
                        className={`btn ${
                            isAtDefault ? "btn-outline-warning" : "btn-warning"
                        } btn-sm`}
                        type="button"
                        onClick={handleRevertToDefault}
                        title={
                            isAtDefault
                                ? "Already at default value"
                                : `Revert to default (${defaultValue})`
                        }
                        style={{
                            fontSize: "12px",
                            padding: "4px 8px",
                            opacity: isAtDefault && !isLoading ? 0.5 : 1,
                        }}
                        disabled={isLoading || isAtDefault}
                    >
                        {isLoading ? (
                            <i className="fa fa-spinner fa-spin"></i>
                        ) : (
                            <i className="fa fa-undo"></i>
                        )}
                    </button>
                );
            },
        },
    ];

    const visibleColumns = columns.filter((_, index) => {
        if (index === 0) return true;
        return showColumns[index - 1];
    });

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
        cells: {
            style: {
                padding: "8px 12px",
                overflow: "visible",
                width: "100%",
                minWidth: "0",
                maxWidth: "none",
            },
        },
        table: {
            style: {
                tableLayout: "fixed",
                width: "100%",
            },
        },
        rows: {
            style: {
                width: "100%",
            },
        },
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-cog fa-10x text-success"></i>
            </div>
            <Spinner />
        </div>
    ) : (
        <div className="container-fluid">
            {settings && (
                <>
                    {/* Category Buttons */}
                    <div className="mb-4 col-12">
                        <div className="d-flex gap-2 flex-wrap">
                            {categories.map((category) => (
                                <button
                                    key={category}
                                    className={`btn ${
                                        selectedCategory === category
                                            ? "btn-success"
                                            : "btn-outline-success"
                                    }`}
                                    onClick={() =>
                                        setSelectedCategory(category)
                                    }
                                    style={{
                                        textTransform: "capitalize",
                                        fontWeight:
                                            selectedCategory === category
                                                ? "600"
                                                : "400",
                                    }}
                                >
                                    {category === "all"
                                        ? "All Settings"
                                        : category.charAt(0).toUpperCase() +
                                          category.slice(1)}
                                    {selectedCategory !== "all" &&
                                        category !== "all" && (
                                            <span className="badge bg-light text-dark ms-2">
                                                {
                                                    settings.filter(
                                                        (s) =>
                                                            s.meta?.category ===
                                                            category
                                                    ).length
                                                }
                                            </span>
                                        )}
                                    {selectedCategory === "all" &&
                                        category === "all" && (
                                            <span className="badge bg-light text-dark ms-2">
                                                {settings.length}
                                            </span>
                                        )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Search and Filter */}
                    <div className="d-flex align-items-center justify-content-between mb-3 col-12">
                        <div className="d-flex align-items-center col-8">
                            <input
                                type="text"
                                className="border rounded p-2"
                                style={{ width: "100%" }}
                                placeholder="Search settings by name or code..."
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                            <Dropdown
                                className="mx-3"
                                show={dropdownOpen}
                                onToggle={() => setDropdownOpen(!dropdownOpen)}
                            >
                                <Dropdown.Toggle
                                    variant="outline-success"
                                    id="dropdown-basic"
                                >
                                    <i className="fa fa-filter me-2"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {["Value", "Revert"].map(
                                        (columnName, i) => (
                                            <Dropdown.Item key={columnName}>
                                                <label className="d-flex align-items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={showColumns[i]}
                                                        onChange={() =>
                                                            toggleColumnVisibility(
                                                                i
                                                            )
                                                        }
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        className="me-2"
                                                    />
                                                    {columnName}
                                                </label>
                                            </Dropdown.Item>
                                        )
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>

                    {/* Settings Count Info */}
                    {selectedCategory !== "all" && (
                        <div className="mb-2 col-12">
                            <small className="text-muted">
                                Showing {filteredItems.length} of{" "}
                                {
                                    settings.filter(
                                        (s) =>
                                            s.meta?.category ===
                                            selectedCategory
                                    ).length
                                }{" "}
                                settings in <strong>{selectedCategory}</strong>{" "}
                                category
                            </small>
                        </div>
                    )}

                    {/* Data Table */}
                    <DataTable
                        columns={visibleColumns}
                        data={filteredItems}
                        pagination
                        highlightOnHover
                        striped
                        responsive
                        defaultSortField="created_at"
                        defaultSortAsc={false}
                        customStyles={customStyles}
                        noDataComponent={
                            <div className="text-center py-5">
                                <p className="text-muted">
                                    No settings found
                                    {filterText && " matching your search"}
                                </p>
                            </div>
                        }
                    />
                </>
            )}
        </div>
    );
};

export { Settings };
